import {
  AlertTriangle,
  CalendarClock,
  Flag,
  FolderTree,
  RotateCcw,
  UserRound,
} from "lucide-react";
import {
  formatItDateTime,
  getItStateLabel,
  type ItEstado,
  type ItIncident,
} from "../types/it";

type Props = {
  incident: ItIncident;
  compact?: boolean;
  draggable?: boolean;
  dragging?: boolean;
  dropPosition?: "before" | "after" | null;
  onClick: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onDragOver?: (event: React.DragEvent<HTMLButtonElement>) => void;
  onDrop?: (event: React.DragEvent<HTMLButtonElement>) => void;
};

const stateTone: Record<
  ItEstado,
  {
    badge: string;
    code: string;
    glow: string;
    line: string;
    drop: string;
    userIcon: string;
  }
> = {
  solicitada: {
    badge: "border-sky-100 bg-sky-50 text-sky-700",
    code: "text-sky-700",
    glow: "group-hover:bg-sky-300/16",
    line: "via-sky-300/70",
    drop: "border-sky-400",
    userIcon: "text-sky-600",
  },
  aprobada: {
    badge: "border-blue-100 bg-blue-50 text-blue-700",
    code: "text-blue-700",
    glow: "group-hover:bg-blue-300/16",
    line: "via-blue-300/70",
    drop: "border-blue-400",
    userIcon: "text-blue-600",
  },
  creada: {
    badge: "border-indigo-100 bg-indigo-50 text-indigo-700",
    code: "text-indigo-700",
    glow: "group-hover:bg-indigo-300/16",
    line: "via-indigo-300/70",
    drop: "border-indigo-400",
    userIcon: "text-indigo-600",
  },
  iniciada: {
    badge: "border-violet-100 bg-violet-50 text-violet-700",
    code: "text-violet-700",
    glow: "group-hover:bg-violet-300/16",
    line: "via-violet-300/70",
    drop: "border-violet-400",
    userIcon: "text-violet-600",
  },
  corregida: {
    badge: "border-cyan-100 bg-cyan-50 text-cyan-700",
    code: "text-cyan-700",
    glow: "group-hover:bg-cyan-300/16",
    line: "via-cyan-300/70",
    drop: "border-cyan-400",
    userIcon: "text-cyan-600",
  },
  rechazada: {
    badge: "border-rose-100 bg-rose-50 text-rose-700",
    code: "text-rose-700",
    glow: "group-hover:bg-rose-300/16",
    line: "via-rose-300/70",
    drop: "border-rose-400",
    userIcon: "text-rose-600",
  },
  cerrada: {
    badge: "border-slate-200 bg-slate-50 text-slate-700",
    code: "text-slate-700",
    glow: "group-hover:bg-slate-300/18",
    line: "via-slate-300/70",
    drop: "border-slate-400",
    userIcon: "text-slate-500",
  },
  incidencia_rechazada: {
    badge: "border-red-100 bg-red-50 text-red-700",
    code: "text-red-700",
    glow: "group-hover:bg-red-300/14",
    line: "via-red-300/70",
    drop: "border-red-400",
    userIcon: "text-red-600",
  },
};

export function ItTicketCard({
  incident,
  compact = false,
  draggable = false,
  dragging = false,
  dropPosition = null,
  onClick,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: Props) {
  const state = stateTone[incident.estado];

  const priorityTone =
    incident.prioridad === "alta"
      ? "border-rose-100 bg-rose-50 text-rose-700"
      : incident.prioridad === "media"
        ? "border-amber-100 bg-amber-50 text-amber-700"
        : "border-blue-100 bg-blue-50 text-blue-700";

  return (
    <button
      type="button"
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={onClick}
      className={[
        "group relative w-full overflow-hidden rounded-[28px] border bg-white text-left shadow-[0_14px_34px_rgba(15,23,42,.065)] transition-all duration-300 hover:-translate-y-1.5 hover:border-blue-200 hover:shadow-[0_26px_58px_rgba(15,23,42,.12)]",
        compact ? "p-4" : "p-5",
        dragging ? "scale-[0.98] opacity-70 ring-4 ring-blue-100" : "",
        incident.veces_reabierta > 0
          ? "border-amber-100 bg-[linear-gradient(135deg,rgba(245,158,11,.065),rgba(255,255,255,.97))]"
          : "border-slate-200",
        dropPosition === "before" ? `border-t-4 border-t-current ${state.drop}` : "",
        dropPosition === "after" ? `border-b-4 border-b-current ${state.drop}` : "",
      ].join(" ")}
    >
      <div
        className={[
          "pointer-events-none absolute right-[-70px] top-[-80px] h-44 w-44 rounded-full bg-transparent blur-3xl transition duration-500",
          state.glow,
        ].join(" ")}
      />

      <div
        className={[
          "absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent to-transparent opacity-0 transition duration-300 group-hover:opacity-100",
          state.line,
        ].join(" ")}
      />

      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div
            className={[
              "text-[11px] font-black uppercase tracking-[0.18em]",
              state.code,
            ].join(" ")}
          >
            {incident.codigo}
          </div>

          <div className="mt-2 line-clamp-2 text-base font-black tracking-tight text-slate-950">
            {incident.titulo}
          </div>
        </div>

        <span
          className={[
            "shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-black shadow-[0_8px_20px_rgba(15,23,42,.04)]",
            state.badge,
          ].join(" ")}
        >
          {getItStateLabel(incident.estado)}
        </span>
      </div>

      {(incident.veces_reabierta > 0 || !compact) && (
        <div className="relative mt-3 flex flex-wrap gap-2">
          {incident.veces_reabierta_mala_correccion > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-black text-amber-800">
              <AlertTriangle size={12} />
              Rechazada · {incident.veces_reabierta_mala_correccion}
            </span>
          )}

          {incident.veces_reabierta_cerrada > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-black text-violet-800">
              <RotateCcw size={12} />
              Reabiertas ({incident.veces_reabierta_cerrada})
            </span>
          )}
        </div>
      )}

      <p
        className={[
          "relative mt-3 text-sm font-semibold leading-6 text-slate-500",
          compact ? "line-clamp-2" : "line-clamp-3",
        ].join(" ")}
      >
        {incident.descripcion || "Sin descripción"}
      </p>

      <div className="relative mt-4 flex flex-wrap gap-2">
        <span
          className={[
            "inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-black",
            priorityTone,
          ].join(" ")}
        >
          <Flag size={12} />
          {incident.prioridad.toUpperCase()}
        </span>

        <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-black text-slate-600">
          <FolderTree size={12} />
          {incident.categoria || "Sin categoría"}
        </span>
      </div>

      <div className="relative mt-4 grid gap-2 rounded-[22px] border border-slate-200 bg-slate-50/70 p-3 text-xs font-semibold text-slate-500 transition-all duration-300 group-hover:bg-white">
        <div className="flex items-center gap-2">
          <UserRound size={13} className="text-slate-400" />

          <span className="truncate">
            Creador:{" "}
            <strong className="text-slate-700">
              {incident.usuario_alta_nombre || "—"}
            </strong>
          </span>
        </div>

        <div className="flex items-center gap-2">
          <UserRound size={13} className={state.userIcon} />

          <span className="truncate">
            Responsable:{" "}
            <strong className="text-slate-700">
              {incident.responsable_nombre || "Sin asignar"}
            </strong>
          </span>
        </div>

        {!compact && (
          <div className="flex items-center gap-2">
            <CalendarClock size={13} className="text-slate-400" />

            <span className="truncate">
              Alta:{" "}
              <strong className="text-slate-700">
                {formatItDateTime(incident.fecha_alta)}
              </strong>
            </span>
          </div>
        )}
      </div>
    </button>
  );
}